import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, role, loading, isImpersonating, mfaRequired } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground text-sm">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Session exists but the 2FA challenge has not been completed yet
  if (mfaRequired) return <Navigate to="/login" replace />;

  // Superadmin NOT impersonating trying to access empresa routes → redirect to superadmin dashboard
  // (a Política de Privacidade é acessível a qualquer perfil)
  if (
    role === 'superadmin' &&
    !isImpersonating &&
    !location.pathname.startsWith('/superadmin') &&
    location.pathname !== '/privacidade'
  ) {
    return <Navigate to="/superadmin/dashboard" replace />;
  }

  // Empresa roles trying to access superadmin routes → redirect to dashboard
  if (role && role !== 'superadmin' && location.pathname.startsWith('/superadmin')) {
    return <Navigate to="/dashboard" replace />;
  }

  // Role-based access check (superadmin bypasses when impersonating)
  if (allowedRoles && role) {
    if (role === 'superadmin') {
      // Superadmin can access anything (impersonating or superadmin routes)
      return <>{children}</>;
    }
    if (!allowedRoles.includes(role)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
}
